import { useQuery, useMutation } from '@tanstack/react-query';

import { clientApi } from '@tankef/api-client';
import type { UpdateAccessibilitySettingsRequest } from '@tankef/core';

const STORAGE_KEY = 'tkf_a11y';

type A11ySettings = {
  font_scale?: number;
  high_contrast?: boolean;
  reduced_motion?: boolean;
  screen_reader_optimized?: boolean;
};

function readStorage(): A11ySettings {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}');
  } catch {
    return {};
  }
}

function writeStorage(patch: A11ySettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...readStorage(), ...patch }));
  } catch {
    // localStorage may be blocked (private mode, quota exceeded) — fail silently
  }
}

/**
 * Hook para obtener configuraciones de accesibilidad
 * GET /api/v1/settings/accessibility
 * Falls back to localStorage when the endpoint is unavailable.
 */
export function useAccessibilitySettings() {
  return useQuery({
    queryKey: ['settings', 'accessibility'],
    queryFn: () => clientApi.getAccessibilitySettings(),
    staleTime: 5 * 60_000,
  });
}

/**
 * Hook para actualizar configuraciones de accesibilidad
 * PATCH /api/v1/settings/accessibility
 * Always persists to localStorage so settings survive even if the API call fails.
 */
export function useUpdateAccessibilitySettings() {
  return useMutation({
    mutationFn: (data: UpdateAccessibilitySettingsRequest) =>
      clientApi.updateAccessibilitySettings(data as Record<string, unknown>),
    // No invalidation needed: settings are applied to the DOM and localStorage
    // immediately in AccessibilitySection, so a refetch would only add a round-trip.
  });
}

/**
 * Aplicar configuraciones de accesibilidad al DOM y persistir en localStorage.
 * Called both from AppInitializer (on load) and AccessibilitySection (on toggle)
 * so changes are always reflected immediately without waiting for an API round-trip.
 */
export function applyAccessibilitySettings(settings: A11ySettings): void {
  if (typeof document === 'undefined') return;

  const root = document.documentElement;

  if (settings.font_scale !== undefined && settings.font_scale !== null) {
    root.style.setProperty('--font-scale', settings.font_scale.toString());
  }

  if (settings.high_contrast !== undefined) {
    root.classList.toggle('high-contrast', settings.high_contrast);
  }

  if (settings.reduced_motion !== undefined) {
    root.classList.toggle('reduce-motion', settings.reduced_motion);
  }

  if (settings.screen_reader_optimized !== undefined) {
    root.setAttribute(
      'aria-label',
      settings.screen_reader_optimized ? 'Screen reader mode enabled' : ''
    );
  }

  // Persist so the next page load can restore settings before the API responds
  writeStorage(settings);
}

/**
 * Restore accessibility settings from localStorage synchronously.
 * Call this once on app mount so the UI is correct before the API query resolves.
 */
export function restoreAccessibilitySettings(): void {
  const stored = readStorage();
  if (Object.keys(stored).length > 0) {
    applyAccessibilitySettings(stored);
  }
}
